import React from 'react';
import { SalesLayout } from '@/components/SalesLayout';
import axios from 'axios';
import { toast } from 'sonner';
import { Pencil, Trash2, X, Check, Search, Plus, ChevronDown, ChevronRight } from 'lucide-react';
import { usePermissions, RESOURCES } from '@/contexts/PermissionsContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Vehicle Hierarchy consolidated view component
function VehicleHierarchyView({ getAuthHeader, canCreate, canDelete, canUpdate }) {
  const [brands, setBrands] = React.useState([]);
  const [models, setModels] = React.useState([]);
  const [variants, setVariants] = React.useState([]);
  const [colors, setColors] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [showAddForm, setShowAddForm] = React.useState(false);
  const [addType, setAddType] = React.useState('brand');
  const [newName, setNewName] = React.useState('');
  const [selectedBrand, setSelectedBrand] = React.useState('');
  const [selectedModel, setSelectedModel] = React.useState('');
  const [selectedVariant, setSelectedVariant] = React.useState('');

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [brandsRes, modelsRes, variantsRes, colorsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/masters?type=brand`, getAuthHeader()),
        axios.get(`${BACKEND_URL}/api/masters?type=model`, getAuthHeader()),
        axios.get(`${BACKEND_URL}/api/masters?type=variant`, getAuthHeader()),
        axios.get(`${BACKEND_URL}/api/masters?type=color`, getAuthHeader()),
      ]);
      setBrands(brandsRes.data);
      setModels(modelsRes.data);
      setVariants(variantsRes.data);
      setColors(colorsRes.data);
    } catch {
      toast.error('Failed to fetch vehicle masters');
    }
    setLoading(false);
  };

  React.useEffect(() => { fetchAll(); }, []);

  // Build hierarchy rows: Brand → Model → Variant → Color
  const hierarchyRows = React.useMemo(() => {
    const rows = [];
    
    brands.forEach(brand => {
      const brandModels = models.filter(m => m.parent_id === brand.id);
      
      if (brandModels.length === 0) {
        rows.push({ brand, model: null, variant: null, color: null });
      } else {
        brandModels.forEach(model => {
          const modelVariants = variants.filter(v => v.parent_id === model.id);
          
          if (modelVariants.length === 0) {
            rows.push({ brand, model, variant: null, color: null });
          } else {
            modelVariants.forEach(variant => {
              const variantColors = colors.filter(c => c.parent_id === variant.id);
              
              if (variantColors.length === 0) {
                rows.push({ brand, model, variant, color: null });
              } else {
                variantColors.forEach(color => {
                  rows.push({ brand, model, variant, color });
                });
              }
            });
          }
        });
      }
    });
    
    return rows;
  }, [brands, models, variants, colors]);

  // Filter rows by search query
  const filteredRows = React.useMemo(() => {
    if (!searchQuery.trim()) return hierarchyRows;
    const q = searchQuery.toLowerCase();
    return hierarchyRows.filter(row => 
      row.brand?.name?.toLowerCase().includes(q) ||
      row.model?.name?.toLowerCase().includes(q) ||
      row.variant?.name?.toLowerCase().includes(q) ||
      row.color?.name?.toLowerCase().includes(q)
    );
  }, [hierarchyRows, searchQuery]);

  const handleAdd = async () => {
    if (!newName.trim()) {
      toast.error('Name is required');
      return;
    }

    let parentId = null;
    if (addType === 'model') {
      if (!selectedBrand) { toast.error('Select a brand first'); return; }
      parentId = selectedBrand;
    } else if (addType === 'variant') {
      if (!selectedModel) { toast.error('Select a model first'); return; }
      parentId = selectedModel;
    } else if (addType === 'color') {
      if (!selectedVariant) { toast.error('Select a variant first'); return; }
      parentId = selectedVariant;
    }

    try {
      await axios.post(`${BACKEND_URL}/api/masters`, {
        type: addType,
        name: newName.trim(),
        parent_id: parentId,
      }, getAuthHeader());
      toast.success(`${addType.charAt(0).toUpperCase() + addType.slice(1)} added successfully`);
      setNewName('');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add');
    }
  };

  const handleDelete = async (item, type) => {
    if (!window.confirm(`Delete "${item.name}" (${type})?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/masters/${item.id}`, getAuthHeader());
      toast.success('Deleted successfully');
      fetchAll();
    } catch {
      toast.error('Failed to delete');
    }
  };

  // Get filtered models/variants based on cascading selection
  const filteredModelsForAdd = models.filter(m => m.parent_id === selectedBrand);
  const filteredVariantsForAdd = variants.filter(v => v.parent_id === selectedModel);

  if (loading) return <div className="p-8 text-center text-gray-500">Loading vehicle hierarchy...</div>;

  return (
    <div data-testid="vehicle-hierarchy-view">
      {/* Search Bar */}
      <div className="mb-4 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search brands, models, variants, colors..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            data-testid="vehicle-hierarchy-search"
          />
        </div>
        {canCreate(RESOURCES.MASTERS) && (
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium whitespace-nowrap"
            data-testid="vehicle-hierarchy-add-btn"
          >
            <Plus className="w-4 h-4" />
            Add New
          </button>
        )}
      </div>

      {/* Add Form */}
      {showAddForm && (
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg" data-testid="vehicle-hierarchy-add-form">
          <h4 className="font-semibold text-sm mb-3 text-blue-800">Add New Vehicle Master</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            <div>
              <label className="block text-xs font-medium mb-1 text-gray-700">Type</label>
              <select
                value={addType}
                onChange={(e) => {
                  setAddType(e.target.value);
                  setSelectedBrand('');
                  setSelectedModel('');
                  setSelectedVariant('');
                  setNewName('');
                }}
                className="w-full p-2 border rounded text-sm"
                data-testid="hierarchy-add-type"
              >
                <option value="brand">Brand</option>
                <option value="model">Model</option>
                <option value="variant">Variant</option>
                <option value="color">Color</option>
              </select>
            </div>

            {(addType === 'model' || addType === 'variant' || addType === 'color') && (
              <div>
                <label className="block text-xs font-medium mb-1 text-gray-700">Brand</label>
                <select
                  value={selectedBrand}
                  onChange={(e) => { setSelectedBrand(e.target.value); setSelectedModel(''); setSelectedVariant(''); }}
                  className="w-full p-2 border rounded text-sm"
                  data-testid="hierarchy-add-brand"
                >
                  <option value="">Select Brand</option>
                  {brands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
            )}

            {(addType === 'variant' || addType === 'color') && (
              <div>
                <label className="block text-xs font-medium mb-1 text-gray-700">Model</label>
                <select
                  value={selectedModel}
                  onChange={(e) => { setSelectedModel(e.target.value); setSelectedVariant(''); }}
                  className={`w-full p-2 border rounded text-sm ${!selectedBrand ? 'bg-gray-100' : ''}`}
                  disabled={!selectedBrand}
                  data-testid="hierarchy-add-model"
                >
                  <option value="">{selectedBrand ? 'Select Model' : 'Select Brand first'}</option>
                  {filteredModelsForAdd.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </div>
            )}

            {addType === 'color' && (
              <div>
                <label className="block text-xs font-medium mb-1 text-gray-700">Variant</label>
                <select
                  value={selectedVariant}
                  onChange={(e) => setSelectedVariant(e.target.value)}
                  className={`w-full p-2 border rounded text-sm ${!selectedModel ? 'bg-gray-100' : ''}`}
                  disabled={!selectedModel}
                  data-testid="hierarchy-add-variant"
                >
                  <option value="">{selectedModel ? 'Select Variant' : 'Select Model first'}</option>
                  {filteredVariantsForAdd.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
              </div>
            )}

            <div>
              <label className="block text-xs font-medium mb-1 text-gray-700">
                {addType.charAt(0).toUpperCase() + addType.slice(1)} Name *
              </label>
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder={`Enter ${addType} name`}
                className="w-full p-2 border rounded text-sm"
                onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
                data-testid="hierarchy-add-name"
              />
            </div>

            <div className="flex items-end">
              <button
                onClick={handleAdd}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm font-medium"
                data-testid="hierarchy-add-submit"
              >
                Add {addType.charAt(0).toUpperCase() + addType.slice(1)}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Summary */}
      <div className="mb-3 flex gap-4 text-xs text-gray-500">
        <span>{brands.length} Brands</span>
        <span>{models.filter(m => m.parent_id).length} Models (linked)</span>
        <span>{variants.filter(v => v.parent_id).length} Variants (linked)</span>
        <span>{colors.filter(c => c.parent_id).length} Colors (linked)</span>
        <span className="ml-auto font-medium text-gray-700">{filteredRows.length} rows</span>
      </div>

      {/* Hierarchy Table */}
      <div className="overflow-x-auto border rounded-lg">
        <table className="w-full text-sm" data-testid="vehicle-hierarchy-table">
          <thead className="text-white" style={{ background: 'linear-gradient(135deg, #4338CA 0%, #4F46E5 50%, #6D28D9 100%)' }}>
            <tr>
              <th className="text-left px-4 py-3 font-semibold text-white tracking-wide text-[12px] uppercase border-b border-white/20">#</th>
              <th className="text-left px-4 py-3 font-semibold text-white tracking-wide text-[12px] uppercase border-b border-white/20">Brand</th>
              <th className="text-left px-4 py-3 font-semibold text-white tracking-wide text-[12px] uppercase border-b border-white/20">Model</th>
              <th className="text-left px-4 py-3 font-semibold text-white tracking-wide text-[12px] uppercase border-b border-white/20">Variant</th>
              <th className="text-left px-4 py-3 font-semibold text-white tracking-wide text-[12px] uppercase border-b border-white/20">Color</th>
              {(canDelete(RESOURCES.MASTERS)) && (
                <th className="text-center px-4 py-3 font-semibold text-white tracking-wide text-[12px] uppercase border-b border-white/20 w-20">Actions</th>
              )}
            </tr>
          </thead>
          <tbody>
            {filteredRows.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center py-8 text-gray-400">
                  {searchQuery ? 'No results found' : 'No vehicle hierarchy data. Add brands, models, variants, and colors to see them here.'}
                </td>
              </tr>
            ) : (
              filteredRows.map((row, idx) => (
                <tr key={`${row.brand?.id}-${row.model?.id}-${row.variant?.id}-${row.color?.id}-${idx}`}
                    className="border-b hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-2.5 text-gray-400">{idx + 1}</td>
                  <td className="px-4 py-2.5 font-medium text-gray-900">{row.brand?.name || '-'}</td>
                  <td className="px-4 py-2.5 text-gray-700">{row.model?.name || <span className="text-gray-300 italic">No model</span>}</td>
                  <td className="px-4 py-2.5 text-gray-700">{row.variant?.name || <span className="text-gray-300 italic">No variant</span>}</td>
                  <td className="px-4 py-2.5 text-gray-700">{row.color?.name || <span className="text-gray-300 italic">No color</span>}</td>
                  {(canDelete(RESOURCES.MASTERS)) && (
                    <td className="px-4 py-2.5 text-center">
                      {row.color && (
                        <button onClick={() => handleDelete(row.color, 'color')} className="text-red-400 hover:text-red-600 p-1" title="Delete color">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Initial state for company fields
const getInitialCompanyData = () => ({
  name: '',
  logo: '',
  uin_no: '',
  gst_no: '',
  building_name: '',
  door_no: '',
  address_1: '',
  address_2: '',
  area: '',
  city: '',
  state: '',
  pincode: '',
  email: '',
  website: '',
  landline_1: '',
  landline_2: '',
  mobile: '',
  mobile_2: '',
  hashtag: '',
});

function MastersManagement({ user, onLogout }) {
  const [activeTab, setActiveTab] = React.useState('vehicle_hierarchy');
  const { canCreate, canUpdate, canDelete } = usePermissions();
  const [masters, setMasters] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [newItem, setNewItem] = React.useState({ 
    name: '', price: '', code: '', mobile: '', address: '', parent_id: '',
    // Sales Rep fields
    emp_mobile: '', emp_door_no: '', emp_address_1: '', emp_address_2: '',
    emp_city: '', emp_state: '', emp_pincode: '', emp_email: ''
  });
  const [companyData, setCompanyData] = React.useState(getInitialCompanyData());
  const [editingId, setEditingId] = React.useState(null);
  const [editData, setEditData] = React.useState({});
  
  // Parent options for cascading hierarchy
  const [parentOptions, setParentOptions] = React.useState([]);

  // Auth header for API calls
  const getAuthHeader = () => {
    const token = localStorage.getItem('access_token') || localStorage.getItem('token');
    return {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    };
  };

  const masterTypes = [
    { value: 'vehicle_hierarchy', label: 'Vehicle Hierarchy' },
    { value: 'brand', label: 'Brands' },
    { value: 'model', label: 'Models' },
    { value: 'variant', label: 'Variants' },
    { value: 'color', label: 'Colors' },
    { value: 'rto', label: 'RTO Details' },
    { value: 'financier', label: 'Financiers' },
    { value: 'sales_rep', label: 'Sales Reps' },
    { value: 'service_technician', label: 'Service Technicians' },
    { value: 'lead_source', label: 'Lead Sources' },
    { value: 'address_proof', label: 'Address Proofs' },
    { value: 'accessory', label: 'Accessories' },
    { value: 'insurance_company', label: 'Insurance Companies' },
    { value: 'insurance_agent', label: 'Insurance Agents' },
    { value: 'company', label: 'Company Details' },
  ];

  React.useEffect(() => {
    if (activeTab !== 'vehicle_hierarchy') {
      fetchMasters();
      fetchParentOptions();
    }
    // Reset form when tab changes
    setNewItem({ 
      name: '', price: '', code: '', mobile: '', address: '', parent_id: '',
      emp_mobile: '', emp_door_no: '', emp_address_1: '', emp_address_2: '',
      emp_city: '', emp_state: '', emp_pincode: '', emp_email: ''
    });
    setCompanyData(getInitialCompanyData());
    setEditingId(null);
  }, [activeTab]);

  // Fetch parent options for cascading dropdowns
  const fetchParentOptions = async () => {
    let parentType = '';
    if (activeTab === 'model') parentType = 'brand';
    else if (activeTab === 'variant') parentType = 'model';
    else if (activeTab === 'color') parentType = 'variant';
    
    if (parentType) {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/masters?type=${parentType}`, getAuthHeader());
        setParentOptions(response.data);
      } catch (error) {
        console.error('Failed to fetch parent options:', error);
        setParentOptions([]);
      }
    } else {
      setParentOptions([]);
    }
  };

  const fetchMasters = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/masters?type=${activeTab}`, getAuthHeader());
      setMasters(response.data);
    } catch (error) {
      toast.error('Failed to fetch master data');
    }
    setLoading(false);
  };

  const handleAdd = async () => {
    if (activeTab === 'company') {
      if (!companyData.name.trim()) {
        toast.error('Company Name is required');
        return;
      }
      try {
        await axios.post(`${BACKEND_URL}/api/masters`, {
          type: 'company',
          ...companyData
        }, getAuthHeader());
        setCompanyData(getInitialCompanyData());
        fetchMasters();
        toast.success('Company details added successfully');
      } catch (error) {
        toast.error('Failed to add company details');
      }
      return;
    }

    if (!newItem.name.trim()) {
      toast.error('Name is required');
      return;
    }

    try {
      const masterData = {
        type: activeTab,
        name: newItem.name,
        price: newItem.price ? parseFloat(newItem.price) : null,
        code: newItem.code || null,
        mobile: newItem.mobile || null,
        address: newItem.address || null,
        parent_id: newItem.parent_id || null,
      };

      // Add Sales Rep or Service Technician specific fields
      if (activeTab === 'sales_rep' || activeTab === 'service_technician') {
        masterData.emp_mobile = newItem.emp_mobile || null;
        masterData.emp_door_no = newItem.emp_door_no || null;
        masterData.emp_address_1 = newItem.emp_address_1 || null;
        masterData.emp_address_2 = newItem.emp_address_2 || null;
        masterData.emp_city = newItem.emp_city || null;
        masterData.emp_state = newItem.emp_state || null;
        masterData.emp_pincode = newItem.emp_pincode || null;
        masterData.emp_email = newItem.emp_email || null;
      }

      await axios.post(`${BACKEND_URL}/api/masters`, masterData, getAuthHeader());
      setNewItem({ 
        name: '', price: '', code: '', mobile: '', address: '', parent_id: '',
        emp_mobile: '', emp_door_no: '', emp_address_1: '', emp_address_2: '',
        emp_city: '', emp_state: '', emp_pincode: '', emp_email: ''
      });
      fetchMasters();
      toast.success('Master data added successfully');
    } catch (error) {
      toast.error('Failed to add master data');
    }
  };

  // Handle logo file upload and convert to base64
  const handleLogoUpload = (e, isEdit = false) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image size should be less than 2MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target.result;
      if (isEdit) {
        setEditData(prev => ({ ...prev, logo: base64 }));
      } else {
        setCompanyData(prev => ({ ...prev, logo: base64 }));
      }
      toast.success('Logo uploaded successfully');
    };
    reader.readAsDataURL(file);
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    if (activeTab === 'company') {
      setEditData({
        name: item.name || '',
        logo: item.logo || '',
        uin_no: item.uin_no || '',
        gst_no: item.gst_no || '',
        building_name: item.building_name || '',
        door_no: item.door_no || '',
        address_1: item.address_1 || '',
        address_2: item.address_2 || '',
        area: item.area || '',
        city: item.city || '',
        state: item.state || '',
        pincode: item.pincode || '',
        email: item.email || '',
        website: item.website || '',
        landline_1: item.landline_1 || '',
        landline_2: item.landline_2 || '',
        mobile: item.mobile || '',
        mobile_2: item.mobile_2 || '',
        hashtag: item.hashtag || '',
      });
    } else if (activeTab === 'sales_rep' || activeTab === 'service_technician') {
      setEditData({
        name: item.name || '',
        emp_mobile: item.emp_mobile || '',
        emp_email: item.emp_email || '',
        emp_door_no: item.emp_door_no || '',
        emp_address_1: item.emp_address_1 || '',
        emp_address_2: item.emp_address_2 || '',
        emp_city: item.emp_city || '',
        emp_state: item.emp_state || '',
        emp_pincode: item.emp_pincode || '',
      });
    } else {
      setEditData({
        name: item.name || '',
        price: item.price || '',
        code: item.code || '',
        mobile: item.mobile || '',
        address: item.address || '',
        parent_id: item.parent_id || '',
      });
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditData({});
  };

  const handleSaveEdit = async (id) => {
    if (!editData.name.trim()) {
      toast.error('Name is required');
      return;
    }

    try {
      if (activeTab === 'company') {
        await axios.put(`${BACKEND_URL}/api/masters/${id}`, {
          type: 'company',
          ...editData
        }, getAuthHeader());
      } else {
        await axios.put(`${BACKEND_URL}/api/masters/${id}`, {
          type: activeTab,
          name: editData.name,
          price: editData.price ? parseFloat(editData.price) : null,
          code: editData.code || null,
          mobile: editData.mobile || null,
          address: editData.address || null,
          parent_id: editData.parent_id || null,
          // Sales rep fields
          emp_mobile: editData.emp_mobile || null,
          emp_door_no: editData.emp_door_no || null,
          emp_address_1: editData.emp_address_1 || null,
          emp_address_2: editData.emp_address_2 || null,
          emp_city: editData.emp_city || null,
          emp_state: editData.emp_state || null,
          emp_pincode: editData.emp_pincode || null,
          emp_email: editData.emp_email || null,
        }, getAuthHeader());
      }
      setEditingId(null);
      setEditData({});
      fetchMasters();
      toast.success('Master data updated successfully');
    } catch (error) {
      console.error('Update error:', error);
      toast.error(error.response?.data?.detail || 'Failed to update master data');
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/masters/${id}`, getAuthHeader());
      fetchMasters();
      toast.success('Master data deleted successfully');
    } catch (error) {
      toast.error('Failed to delete master data');
    }
  };

  // Determine which fields are needed for each master type
  const isCompanyTab = activeTab === 'company';
  const isSalesRepTab = activeTab === 'sales_rep';
  const isServiceTechTab = activeTab === 'service_technician';
  const isVehicleHierarchyTab = activeTab === 'vehicle_hierarchy';
  const needsPriceField = ['accessory'].includes(activeTab);
  const needsCodeField = activeTab === 'rto';
  const needsMobileField = ['rto', 'financier'].includes(activeTab);
  const needsAddressField = activeTab === 'financier';
  const needsParentField = ['model', 'variant', 'color'].includes(activeTab);
  
  // Helper to find parent name by ID
  const getParentName = (parentId) => {
    if (!parentId) return '-';
    const parent = parentOptions.find(p => p.id === parentId);
    return parent?.name || '-';
  };

  return (
    <SalesLayout user={user} onLogout={onLogout} title="Masters Management">
      <div className="bg-white p-6 rounded-lg border">
        <h2 className="text-2xl font-bold mb-6">Masters Management</h2>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b flex-wrap overflow-x-auto">
          {masterTypes.map(type => (
            <button
              key={type.value}
              onClick={() => setActiveTab(type.value)}
              className={`px-3 py-2 font-medium transition-colors whitespace-nowrap text-sm ${
                activeTab === type.value
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              {type.label}
            </button>
          ))}
        </div>

        {/* Vehicle Hierarchy Tab */}
        {activeTab === 'vehicle_hierarchy' && (
          <VehicleHierarchyView getAuthHeader={getAuthHeader} canCreate={canCreate} canDelete={canDelete} canUpdate={canUpdate} />
        )}

        {/* Company Add Form - only show if user can create masters */}
        {isCompanyTab && canCreate(RESOURCES.MASTERS) && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-semibold mb-4">Add Company Details (For Report Headers)</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {/* Logo Upload */}
              <div className="md:col-span-1 lg:col-span-1">
                <label className="block text-sm font-medium mb-1">Company Logo</label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                  {companyData.logo ? (
                    <div className="relative">
                      <img src={companyData.logo} alt="Company Logo" className="max-h-24 mx-auto mb-2 object-contain" />
                      <button type="button" onClick={() => setCompanyData(prev => ({...prev, logo: ''}))}
                        className="text-xs text-red-600 hover:text-red-800">Remove</button>
                    </div>
                  ) : (
                    <div>
                      <svg className="mx-auto h-10 w-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <p className="text-xs text-gray-500 mt-1">Max 2MB</p>
                    </div>
                  )}
                  <input type="file" accept="image/*" className="hidden" id="logo-upload"
                    onChange={(e) => handleLogoUpload(e, false)} />
                  <label htmlFor="logo-upload" className="cursor-pointer text-sm text-blue-600 hover:text-blue-800 mt-2 inline-block">
                    {companyData.logo ? 'Change Logo' : 'Upload Logo'}
                  </label>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Company Name *</label>
                <input type="text" className="w-full p-2 border rounded" value={companyData.name}
                  onChange={(e) => setCompanyData(prev => ({...prev, name: e.target.value}))} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">UIN No.</label>
                <input type="text" className="w-full p-2 border rounded" value={companyData.uin_no}
                  onChange={(e) => setCompanyData(prev => ({...prev, uin_no: e.target.value}))} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">GST No.</label>
                <input type="text" className="w-full p-2 border rounded" maxLength={15} value={companyData.gst_no}
                  onChange={(e) => setCompanyData(prev => ({...prev, gst_no: e.target.value.toUpperCase()}))} placeholder="22AAAAA0000A1Z5" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Building Name</label>
                <input type="text" className="w-full p-2 border rounded" value={companyData.building_name}
                  onChange={(e) => setCompanyData(prev => ({...prev, building_name: e.target.value}))} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Door No.</label>
                <input type="text" className="w-full p-2 border rounded" value={companyData.door_no}
                  onChange={(e) => setCompanyData(prev => ({...prev, door_no: e.target.value}))} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Address Line 1</label>
                <input type="text" className="w-full p-2 border rounded" value={companyData.address_1}
                  onChange={(e) => setCompanyData(prev => ({...prev, address_1: e.target.value}))} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Address Line 2</label>
                <input type="text" className="w-full p-2 border rounded" value={companyData.address_2}
                  onChange={(e) => setCompanyData(prev => ({...prev, address_2: e.target.value}))} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Area</label>
                <input type="text" className="w-full p-2 border rounded" value={companyData.area}
                  onChange={(e) => setCompanyData(prev => ({...prev, area: e.target.value}))} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">City</label>
                <input type="text" className="w-full p-2 border rounded" value={companyData.city}
                  onChange={(e) => setCompanyData(prev => ({...prev, city: e.target.value}))} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">State</label>
                <input type="text" className="w-full p-2 border rounded" value={companyData.state}
                  onChange={(e) => setCompanyData(prev => ({...prev, state: e.target.value}))} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Pincode</label>
                <input type="text" className="w-full p-2 border rounded" maxLength={6} value={companyData.pincode}
                  onChange={(e) => setCompanyData(prev => ({...prev, pincode: e.target.value.replace(/\D/g, '')}))} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">E-Mail</label>
                <input type="email" className="w-full p-2 border rounded" value={companyData.email}
                  onChange={(e) => setCompanyData(prev => ({...prev, email: e.target.value}))} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Website</label>
                <input type="text" className="w-full p-2 border rounded" placeholder="www.example.com" value={companyData.website}
                  onChange={(e) => setCompanyData(prev => ({...prev, website: e.target.value}))} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Landline No. 1</label>
                <input type="tel" className="w-full p-2 border rounded" value={companyData.landline_1}
                  onChange={(e) => setCompanyData(prev => ({...prev, landline_1: e.target.value}))} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Landline No. 2</label>
                <input type="tel" className="w-full p-2 border rounded" value={companyData.landline_2}
                  onChange={(e) => setCompanyData(prev => ({...prev, landline_2: e.target.value}))} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Mobile No. 1</label>
                <input type="tel" className="w-full p-2 border rounded" maxLength={10} value={companyData.mobile}
                  onChange={(e) => setCompanyData(prev => ({...prev, mobile: e.target.value.replace(/\D/g, '')}))} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Mobile No. 2</label>
                <input type="tel" className="w-full p-2 border rounded" maxLength={10} value={companyData.mobile_2}
                  onChange={(e) => setCompanyData(prev => ({...prev, mobile_2: e.target.value.replace(/\D/g, '')}))} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Hashtag</label>
                <input type="text" className="w-full p-2 border rounded" placeholder="#yourhashtag" value={companyData.hashtag}
                  onChange={(e) => setCompanyData(prev => ({...prev, hashtag: e.target.value}))} />
              </div>
              <div className="flex items-end">
                <button onClick={handleAdd} className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                  Add Company
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Regular Add Form (for non-company tabs) - only show if user can create masters */}
        {!isCompanyTab && !isSalesRepTab && !isServiceTechTab && !isVehicleHierarchyTab && canCreate(RESOURCES.MASTERS) && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-semibold mb-4">Add New {masterTypes.find(t => t.value === activeTab)?.label}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {/* Parent selector for cascading hierarchy */}
              {['model', 'variant', 'color'].includes(activeTab) && (
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {activeTab === 'model' ? 'Brand (Parent)' : activeTab === 'variant' ? 'Model (Parent)' : 'Variant (Parent)'} *
                  </label>
                  <select 
                    className="w-full p-2 border rounded" 
                    value={newItem.parent_id}
                    onChange={(e) => setNewItem(prev => ({...prev, parent_id: e.target.value}))}
                    data-testid="parent-select"
                  >
                    <option value="">Select {activeTab === 'model' ? 'Brand' : activeTab === 'variant' ? 'Model' : 'Variant'}</option>
                    {parentOptions.map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                  {parentOptions.length === 0 && (
                    <p className="text-xs text-amber-600 mt-1">
                      No {activeTab === 'model' ? 'brands' : activeTab === 'variant' ? 'models' : 'variants'} found. Add them first.
                    </p>
                  )}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium mb-1">Name *</label>
                <input type="text" className="w-full p-2 border rounded" value={newItem.name}
                  onChange={(e) => setNewItem(prev => ({...prev, name: e.target.value}))} placeholder="Enter name" />
              </div>
              {needsPriceField && (
                <div>
                  <label className="block text-sm font-medium mb-1">Price (₹) *</label>
                  <input type="number" step="0.01" min="0" className="w-full p-2 border rounded" value={newItem.price}
                    onChange={(e) => setNewItem(prev => ({...prev, price: e.target.value}))} placeholder="0.00" />
                </div>
              )}
              {needsCodeField && (
                <div>
                  <label className="block text-sm font-medium mb-1">RTO Code</label>
                  <input type="text" className="w-full p-2 border rounded" value={newItem.code}
                    onChange={(e) => setNewItem(prev => ({...prev, code: e.target.value}))} placeholder="TN01" />
                </div>
              )}
              {needsMobileField && (
                <div>
                  <label className="block text-sm font-medium mb-1">Mobile No</label>
                  <input type="tel" maxLength={10} className="w-full p-2 border rounded" value={newItem.mobile}
                    onChange={(e) => setNewItem(prev => ({...prev, mobile: e.target.value.replace(/\D/g, '')}))} placeholder="9876543210" />
                </div>
              )}
              {needsAddressField && (
                <div>
                  <label className="block text-sm font-medium mb-1">Address</label>
                  <input type="text" className="w-full p-2 border rounded" value={newItem.address}
                    onChange={(e) => setNewItem(prev => ({...prev, address: e.target.value}))} placeholder="Enter address" />
                </div>
              )}
              <div className="flex items-end">
                <button onClick={handleAdd} className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                  Add
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Sales Rep Add Form - only show if user can create masters */}
        {isSalesRepTab && canCreate(RESOURCES.MASTERS) && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-semibold mb-4">Add New Sales Representative</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Name *</label>
                <input type="text" className="w-full p-2 border rounded" value={newItem.name}
                  onChange={(e) => setNewItem(prev => ({...prev, name: e.target.value}))} placeholder="Sales Rep Name" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Mobile No *</label>
                <input type="tel" maxLength={10} className="w-full p-2 border rounded" value={newItem.emp_mobile}
                  onChange={(e) => setNewItem(prev => ({...prev, emp_mobile: e.target.value.replace(/\D/g, '')}))} placeholder="9876543210" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Email Id</label>
                <input type="email" className="w-full p-2 border rounded" value={newItem.emp_email}
                  onChange={(e) => setNewItem(prev => ({...prev, emp_email: e.target.value}))} placeholder="email@example.com" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Door No</label>
                <input type="text" className="w-full p-2 border rounded" value={newItem.emp_door_no}
                  onChange={(e) => setNewItem(prev => ({...prev, emp_door_no: e.target.value}))} placeholder="123" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Address Line 1</label>
                <input type="text" className="w-full p-2 border rounded" value={newItem.emp_address_1}
                  onChange={(e) => setNewItem(prev => ({...prev, emp_address_1: e.target.value}))} placeholder="Street name" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Address Line 2</label>
                <input type="text" className="w-full p-2 border rounded" value={newItem.emp_address_2}
                  onChange={(e) => setNewItem(prev => ({...prev, emp_address_2: e.target.value}))} placeholder="Area/Locality" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">City</label>
                <input type="text" className="w-full p-2 border rounded" value={newItem.emp_city}
                  onChange={(e) => setNewItem(prev => ({...prev, emp_city: e.target.value}))} placeholder="City" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">State</label>
                <input type="text" className="w-full p-2 border rounded" value={newItem.emp_state}
                  onChange={(e) => setNewItem(prev => ({...prev, emp_state: e.target.value}))} placeholder="State" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Pincode</label>
                <input type="text" maxLength={6} className="w-full p-2 border rounded" value={newItem.emp_pincode}
                  onChange={(e) => setNewItem(prev => ({...prev, emp_pincode: e.target.value.replace(/\D/g, '')}))} placeholder="600001" />
              </div>
              <div className="flex items-end">
                <button onClick={handleAdd} className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                  Add Sales Rep
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Service Technician Add Form - only show if user can create masters */}
        {isServiceTechTab && canCreate(RESOURCES.MASTERS) && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-semibold mb-4">Add New Service Technician</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Name *</label>
                <input type="text" className="w-full p-2 border rounded" value={newItem.name}
                  onChange={(e) => setNewItem(prev => ({...prev, name: e.target.value}))} placeholder="Technician Name" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Mobile No *</label>
                <input type="tel" maxLength={10} className="w-full p-2 border rounded" value={newItem.emp_mobile}
                  onChange={(e) => setNewItem(prev => ({...prev, emp_mobile: e.target.value.replace(/\D/g, '')}))} placeholder="9876543210" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Email Id</label>
                <input type="email" className="w-full p-2 border rounded" value={newItem.emp_email}
                  onChange={(e) => setNewItem(prev => ({...prev, emp_email: e.target.value}))} placeholder="email@example.com" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Door No</label>
                <input type="text" className="w-full p-2 border rounded" value={newItem.emp_door_no}
                  onChange={(e) => setNewItem(prev => ({...prev, emp_door_no: e.target.value}))} placeholder="123" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Address Line 1</label>
                <input type="text" className="w-full p-2 border rounded" value={newItem.emp_address_1}
                  onChange={(e) => setNewItem(prev => ({...prev, emp_address_1: e.target.value}))} placeholder="Street name" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Address Line 2</label>
                <input type="text" className="w-full p-2 border rounded" value={newItem.emp_address_2}
                  onChange={(e) => setNewItem(prev => ({...prev, emp_address_2: e.target.value}))} placeholder="Area/Locality" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">City</label>
                <input type="text" className="w-full p-2 border rounded" value={newItem.emp_city}
                  onChange={(e) => setNewItem(prev => ({...prev, emp_city: e.target.value}))} placeholder="City" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">State</label>
                <input type="text" className="w-full p-2 border rounded" value={newItem.emp_state}
                  onChange={(e) => setNewItem(prev => ({...prev, emp_state: e.target.value}))} placeholder="State" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Pincode</label>
                <input type="text" maxLength={6} className="w-full p-2 border rounded" value={newItem.emp_pincode}
                  onChange={(e) => setNewItem(prev => ({...prev, emp_pincode: e.target.value.replace(/\D/g, '')}))} placeholder="600001" />
              </div>
              <div className="flex items-end">
                <button onClick={handleAdd} className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                  Add Technician
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Company List Display */}
        {isCompanyTab && (
          loading ? (
            <p>Loading...</p>
          ) : masters.length > 0 ? (
            <div className="space-y-4">
              {masters.map(item => (
                <div key={item.id} className="border rounded-lg p-4 bg-white shadow-sm">
                  {editingId === item.id ? (
                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {/* Logo Edit */}
                      <div className="md:col-span-1">
                        <label className="block text-xs font-medium text-gray-500 mb-1">Company Logo</label>
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-2 text-center">
                          {editData.logo ? (
                            <div className="relative">
                              <img src={editData.logo} alt="Logo" className="max-h-16 mx-auto mb-1 object-contain" />
                              <button type="button" onClick={() => setEditData(prev => ({...prev, logo: ''}))}
                                className="text-xs text-red-600 hover:text-red-800">Remove</button>
                            </div>
                          ) : (
                            <p className="text-xs text-gray-400">No logo</p>
                          )}
                          <input type="file" accept="image/*" className="hidden" id={`logo-edit-${item.id}`}
                            onChange={(e) => handleLogoUpload(e, true)} />
                          <label htmlFor={`logo-edit-${item.id}`} className="cursor-pointer text-xs text-blue-600 hover:text-blue-800">
                            {editData.logo ? 'Change' : 'Upload'}
                          </label>
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Company Name *</label>
                        <input type="text" className="w-full p-2 border rounded text-sm" value={editData.name}
                          onChange={(e) => setEditData(prev => ({...prev, name: e.target.value}))} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">UIN No.</label>
                        <input type="text" className="w-full p-2 border rounded text-sm" value={editData.uin_no}
                          onChange={(e) => setEditData(prev => ({...prev, uin_no: e.target.value}))} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">GST No.</label>
                        <input type="text" className="w-full p-2 border rounded text-sm" maxLength={15} value={editData.gst_no}
                          onChange={(e) => setEditData(prev => ({...prev, gst_no: e.target.value.toUpperCase()}))} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Building Name</label>
                        <input type="text" className="w-full p-2 border rounded text-sm" value={editData.building_name}
                          onChange={(e) => setEditData(prev => ({...prev, building_name: e.target.value}))} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Door No.</label>
                        <input type="text" className="w-full p-2 border rounded text-sm" value={editData.door_no}
                          onChange={(e) => setEditData(prev => ({...prev, door_no: e.target.value}))} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Address 1</label>
                        <input type="text" className="w-full p-2 border rounded text-sm" value={editData.address_1}
                          onChange={(e) => setEditData(prev => ({...prev, address_1: e.target.value}))} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Address 2</label>
                        <input type="text" className="w-full p-2 border rounded text-sm" value={editData.address_2}
                          onChange={(e) => setEditData(prev => ({...prev, address_2: e.target.value}))} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Area</label>
                        <input type="text" className="w-full p-2 border rounded text-sm" value={editData.area}
                          onChange={(e) => setEditData(prev => ({...prev, area: e.target.value}))} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">City</label>
                        <input type="text" className="w-full p-2 border rounded text-sm" value={editData.city}
                          onChange={(e) => setEditData(prev => ({...prev, city: e.target.value}))} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">State</label>
                        <input type="text" className="w-full p-2 border rounded text-sm" value={editData.state}
                          onChange={(e) => setEditData(prev => ({...prev, state: e.target.value}))} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Pincode</label>
                        <input type="text" className="w-full p-2 border rounded text-sm" maxLength={6} value={editData.pincode}
                          onChange={(e) => setEditData(prev => ({...prev, pincode: e.target.value.replace(/\D/g, '')}))} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">E-Mail</label>
                        <input type="email" className="w-full p-2 border rounded text-sm" value={editData.email}
                          onChange={(e) => setEditData(prev => ({...prev, email: e.target.value}))} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Website</label>
                        <input type="text" className="w-full p-2 border rounded text-sm" value={editData.website}
                          onChange={(e) => setEditData(prev => ({...prev, website: e.target.value}))} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Landline 1</label>
                        <input type="tel" className="w-full p-2 border rounded text-sm" value={editData.landline_1}
                          onChange={(e) => setEditData(prev => ({...prev, landline_1: e.target.value}))} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Landline 2</label>
                        <input type="tel" className="w-full p-2 border rounded text-sm" value={editData.landline_2}
                          onChange={(e) => setEditData(prev => ({...prev, landline_2: e.target.value}))} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Mobile 1</label>
                        <input type="tel" className="w-full p-2 border rounded text-sm" maxLength={10} value={editData.mobile}
                          onChange={(e) => setEditData(prev => ({...prev, mobile: e.target.value.replace(/\D/g, '')}))} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Mobile 2</label>
                        <input type="tel" className="w-full p-2 border rounded text-sm" maxLength={10} value={editData.mobile_2}
                          onChange={(e) => setEditData(prev => ({...prev, mobile_2: e.target.value.replace(/\D/g, '')}))} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Hashtag</label>
                        <input type="text" className="w-full p-2 border rounded text-sm" value={editData.hashtag}
                          onChange={(e) => setEditData(prev => ({...prev, hashtag: e.target.value}))} />
                      </div>
                      <div className="flex items-end gap-2">
                        <button onClick={() => handleSaveEdit(item.id)} className="px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm">
                          <Check className="w-4 h-4" />
                        </button>
                        <button onClick={handleCancelEdit} className="px-3 py-2 bg-gray-400 text-white rounded hover:bg-gray-500 text-sm">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-4">
                      {/* Logo Display */}
                      {item.logo && (
                        <div className="flex-shrink-0">
                          <img src={item.logo} alt="Company Logo" className="h-20 w-20 object-contain border rounded p-1" />
                        </div>
                      )}
                      <div className="flex-1">
                        <div className="flex justify-between items-start mb-3">
                          <h4 className="text-lg font-bold text-blue-700">{item.name}</h4>
                          <div className="flex gap-2">
                            {canUpdate(RESOURCES.MASTERS) && (
                              <button onClick={() => handleEdit(item)} className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded" title="Edit">
                                <Pencil className="w-4 h-4" />
                              </button>
                            )}
                            {canDelete(RESOURCES.MASTERS) && (
                              <button onClick={() => handleDelete(item.id, item.name)} className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded" title="Delete">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                          {item.uin_no && <div><span className="text-gray-500">UIN:</span> {item.uin_no}</div>}
                          {item.email && <div><span className="text-gray-500">Email:</span> {item.email}</div>}
                          {item.website && <div><span className="text-gray-500">Website:</span> {item.website}</div>}
                          {item.mobile && <div><span className="text-gray-500">Mobile 1:</span> {item.mobile}</div>}
                        {item.mobile_2 && <div><span className="text-gray-500">Mobile 2:</span> {item.mobile_2}</div>}
                        {item.landline_1 && <div><span className="text-gray-500">Landline 1:</span> {item.landline_1}</div>}
                        {item.landline_2 && <div><span className="text-gray-500">Landline 2:</span> {item.landline_2}</div>}
                        {item.gst_no && <div><span className="text-gray-500">GST No:</span> {item.gst_no}</div>}
                        {item.hashtag && <div><span className="text-gray-500">Hashtag:</span> {item.hashtag}</div>}
                      </div>
                      {(item.building_name || item.address_1 || item.city) && (
                        <div className="mt-2 text-sm text-gray-600">
                          <span className="text-gray-500">Address:</span> {[item.building_name, item.door_no, item.address_1, item.address_2, item.area, item.city, item.state, item.pincode].filter(Boolean).join(', ')}
                        </div>
                      )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-gray-500 py-8">No company details added yet. Add your company information for report headers.</p>
          )
        )}

        {/* Regular List (for non-company, non-sales-rep, non-service-technician tabs) */}
        {!isCompanyTab && !isSalesRepTab && !isServiceTechTab && !isVehicleHierarchyTab && (
          loading ? (
            <p>Loading...</p>
          ) : masters.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="text-white" style={{ background: 'linear-gradient(135deg, #4338CA 0%, #4F46E5 50%, #6D28D9 100%)' }}>
                  <tr>
                    <th className="text-left p-3 border-b border-white/20 font-semibold tracking-wide text-[12px] uppercase">Name</th>
                    {needsParentField && <th className="text-left p-3 border-b border-white/20 font-semibold tracking-wide text-[12px] uppercase">{activeTab === 'model' ? 'Brand' : activeTab === 'variant' ? 'Model' : 'Variant'}</th>}
                    {needsPriceField && <th className="text-left p-3 border-b border-white/20 font-semibold tracking-wide text-[12px] uppercase">Price</th>}
                    {needsCodeField && <th className="text-left p-3 border-b border-white/20 font-semibold tracking-wide text-[12px] uppercase">Code</th>}
                    {needsMobileField && <th className="text-left p-3 border-b border-white/20 font-semibold tracking-wide text-[12px] uppercase">Mobile</th>}
                    {needsAddressField && <th className="text-left p-3 border-b border-white/20 font-semibold tracking-wide text-[12px] uppercase">Address</th>}
                    <th className="text-left p-3 border-b border-white/20 w-32 font-semibold tracking-wide text-[12px] uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {masters.map((item, idx) => (
                    <tr key={item.id} className={`border-b border-indigo-50 hover:bg-gradient-to-r hover:from-indigo-50 hover:to-violet-50 transition-all duration-180 ${idx % 2 === 0 ? 'bg-white' : 'bg-gradient-to-r from-indigo-50/40 to-violet-50/30'}`}>
                      {editingId === item.id ? (
                        <>
                          <td className="p-2">
                            <input type="text" className="w-full p-1 border rounded" value={editData.name}
                              onChange={(e) => setEditData(prev => ({...prev, name: e.target.value}))} />
                          </td>
                          {needsParentField && (
                            <td className="p-2">
                              <select 
                                className="w-full p-1 border rounded" 
                                value={editData.parent_id || ''}
                                onChange={(e) => setEditData(prev => ({...prev, parent_id: e.target.value}))}
                              >
                                <option value="">No Parent</option>
                                {parentOptions.map(p => (
                                  <option key={p.id} value={p.id}>{p.name}</option>
                                ))}
                              </select>
                            </td>
                          )}
                          {needsPriceField && (
                            <td className="p-2">
                              <input type="number" step="0.01" min="0" className="w-full p-1 border rounded" value={editData.price}
                                onChange={(e) => setEditData(prev => ({...prev, price: e.target.value}))} />
                            </td>
                          )}
                          {needsCodeField && (
                            <td className="p-2">
                              <input type="text" className="w-full p-1 border rounded" value={editData.code}
                                onChange={(e) => setEditData(prev => ({...prev, code: e.target.value}))} />
                            </td>
                          )}
                          {needsMobileField && (
                            <td className="p-2">
                              <input type="tel" maxLength={10} className="w-full p-1 border rounded" value={editData.mobile}
                                onChange={(e) => setEditData(prev => ({...prev, mobile: e.target.value.replace(/\D/g, '')}))} />
                            </td>
                          )}
                          {needsAddressField && (
                            <td className="p-2">
                              <input type="text" className="w-full p-1 border rounded" value={editData.address}
                                onChange={(e) => setEditData(prev => ({...prev, address: e.target.value}))} />
                            </td>
                          )}
                          <td className="p-2">
                            <div className="flex gap-2">
                              <button onClick={() => handleSaveEdit(item.id)} className="p-1 text-green-600 hover:text-green-800 hover:bg-green-50 rounded" title="Save">
                                <Check className="w-5 h-5" />
                              </button>
                              <button onClick={handleCancelEdit} className="p-1 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded" title="Cancel">
                                <X className="w-5 h-5" />
                              </button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="p-3">{item.name}</td>
                          {needsParentField && <td className="p-3 text-gray-600">{getParentName(item.parent_id)}</td>}
                          {needsPriceField && <td className="p-3">₹{item.price?.toLocaleString() || '-'}</td>}
                          {needsCodeField && <td className="p-3">{item.code || '-'}</td>}
                          {needsMobileField && <td className="p-3">{item.mobile || '-'}</td>}
                          {needsAddressField && <td className="p-3">{item.address || '-'}</td>}
                          <td className="p-3">
                            <div className="flex gap-2">
                              {canUpdate(RESOURCES.MASTERS) && (
                                <button onClick={() => handleEdit(item)} className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded" title="Edit">
                                  <Pencil className="w-4 h-4" />
                                </button>
                              )}
                              {canDelete(RESOURCES.MASTERS) && (
                                <button onClick={() => handleDelete(item.id, item.name)} className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded" title="Delete">
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-center text-gray-500 py-8">No items added yet. Add your first {masterTypes.find(t => t.value === activeTab)?.label.toLowerCase()}.</p>
          )
        )}

        {/* Sales Rep List Display */}
        {isSalesRepTab && (
          loading ? (
            <p>Loading...</p>
          ) : masters.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-white" style={{ background: 'linear-gradient(135deg, #4338CA 0%, #4F46E5 50%, #6D28D9 100%)' }}>
                  <tr>
                    <th className="text-left p-3 border-b border-white/20 font-semibold tracking-wide text-[12px] uppercase">#</th>
                    <th className="text-left p-3 border-b border-white/20 font-semibold tracking-wide text-[12px] uppercase">Name</th>
                    <th className="text-left p-3 border-b border-white/20 font-semibold tracking-wide text-[12px] uppercase">Mobile</th>
                    <th className="text-left p-3 border-b border-white/20 font-semibold tracking-wide text-[12px] uppercase">Email</th>
                    <th className="text-left p-3 border-b border-white/20 font-semibold tracking-wide text-[12px] uppercase">Door No</th>
                    <th className="text-left p-3 border-b border-white/20 font-semibold tracking-wide text-[12px] uppercase">Address 1</th>
                    <th className="text-left p-3 border-b border-white/20 font-semibold tracking-wide text-[12px] uppercase">Address 2</th>
                    <th className="text-left p-3 border-b border-white/20 font-semibold tracking-wide text-[12px] uppercase">City</th>
                    <th className="text-left p-3 border-b border-white/20 font-semibold tracking-wide text-[12px] uppercase">State</th>
                    <th className="text-left p-3 border-b border-white/20 font-semibold tracking-wide text-[12px] uppercase">Pincode</th>
                    <th className="text-left p-3 border-b border-white/20 w-32 font-semibold tracking-wide text-[12px] uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {masters.map((item, idx) => (
                    <tr key={item.id} className={`border-b border-indigo-50 hover:bg-gradient-to-r hover:from-indigo-50 hover:to-violet-50 transition-all duration-180 ${idx % 2 === 0 ? 'bg-white' : 'bg-gradient-to-r from-indigo-50/40 to-violet-50/30'}`}>
                      {editingId === item.id ? (
                        <>
                          <td className="p-2 text-gray-500">{idx + 1}</td>
                          <td className="p-2">
                            <input type="text" className="w-full p-1 border rounded text-sm" value={editData.name}
                              onChange={(e) => setEditData(prev => ({...prev, name: e.target.value}))} />
                          </td>
                          <td className="p-2">
                            <input type="tel" maxLength={10} className="w-full p-1 border rounded text-sm" value={editData.emp_mobile || ''}
                              onChange={(e) => setEditData(prev => ({...prev, emp_mobile: e.target.value.replace(/\D/g, '')}))} />
                          </td>
                          <td className="p-2">
                            <input type="email" className="w-full p-1 border rounded text-sm" value={editData.emp_email || ''}
                              onChange={(e) => setEditData(prev => ({...prev, emp_email: e.target.value}))} />
                          </td>
                          <td className="p-2">
                            <input type="text" className="w-full p-1 border rounded text-sm" value={editData.emp_door_no || ''}
                              onChange={(e) => setEditData(prev => ({...prev, emp_door_no: e.target.value}))} />
                          </td>
                          <td className="p-2">
                            <input type="text" className="w-full p-1 border rounded text-sm" value={editData.emp_address_1 || ''}
                              onChange={(e) => setEditData(prev => ({...prev, emp_address_1: e.target.value}))} />
                          </td>
                          <td className="p-2">
                            <input type="text" className="w-full p-1 border rounded text-sm" value={editData.emp_address_2 || ''}
                              onChange={(e) => setEditData(prev => ({...prev, emp_address_2: e.target.value}))} />
                          </td>
                          <td className="p-2">
                            <input type="text" className="w-full p-1 border rounded text-sm" value={editData.emp_city || ''}
                              onChange={(e) => setEditData(prev => ({...prev, emp_city: e.target.value}))} />
                          </td>
                          <td className="p-2">
                            <input type="text" className="w-full p-1 border rounded text-sm" value={editData.emp_state || ''}
                              onChange={(e) => setEditData(prev => ({...prev, emp_state: e.target.value}))} />
                          </td>
                          <td className="p-2">
                            <input type="text" maxLength={6} className="w-full p-1 border rounded text-sm" value={editData.emp_pincode || ''}
                              onChange={(e) => setEditData(prev => ({...prev, emp_pincode: e.target.value.replace(/\D/g, '')}))} />
                          </td>
                          <td className="p-2">
                            <div className="flex gap-2">
                              <button onClick={() => handleSaveEdit(item.id)} className="p-1 text-green-600 hover:text-green-800 hover:bg-green-50 rounded" title="Save">
                                <Check className="w-5 h-5" />
                              </button>
                              <button onClick={handleCancelEdit} className="p-1 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded" title="Cancel">
                                <X className="w-5 h-5" />
                              </button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="p-3 text-gray-500">{idx + 1}</td>
                          <td className="p-3 font-medium">{item.name}</td>
                          <td className="p-3">{item.emp_mobile || '-'}</td>
                          <td className="p-3 text-blue-600">{item.emp_email || '-'}</td>
                          <td className="p-3">{item.emp_door_no || '-'}</td>
                          <td className="p-3">{item.emp_address_1 || '-'}</td>
                          <td className="p-3">{item.emp_address_2 || '-'}</td>
                          <td className="p-3">{item.emp_city || '-'}</td>
                          <td className="p-3">{item.emp_state || '-'}</td>
                          <td className="p-3">{item.emp_pincode || '-'}</td>
                          <td className="p-3">
                            <div className="flex gap-2">
                              {canUpdate(RESOURCES.MASTERS) && (
                                <button onClick={() => handleEdit(item)} className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded" title="Edit">
                                  <Pencil className="w-4 h-4" />
                                </button>
                              )}
                              {canDelete(RESOURCES.MASTERS) && (
                                <button onClick={() => handleDelete(item.id, item.name)} className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded" title="Delete">
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-center text-gray-500 py-8">No sales representatives added yet. Add your first sales rep to get started.</p>
          )
        )}

        {/* Service Technician List Display */}
        {isServiceTechTab && (
          loading ? (
            <p>Loading...</p>
          ) : masters.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-white" style={{ background: 'linear-gradient(135deg, #4338CA 0%, #4F46E5 50%, #6D28D9 100%)' }}>
                  <tr>
                    <th className="text-left p-3 border-b border-white/20 font-semibold tracking-wide text-[12px] uppercase">#</th>
                    <th className="text-left p-3 border-b border-white/20 font-semibold tracking-wide text-[12px] uppercase">Name</th>
                    <th className="text-left p-3 border-b border-white/20 font-semibold tracking-wide text-[12px] uppercase">Mobile</th>
                    <th className="text-left p-3 border-b border-white/20 font-semibold tracking-wide text-[12px] uppercase">Email</th>
                    <th className="text-left p-3 border-b border-white/20 font-semibold tracking-wide text-[12px] uppercase">Door No</th>
                    <th className="text-left p-3 border-b border-white/20 font-semibold tracking-wide text-[12px] uppercase">Address 1</th>
                    <th className="text-left p-3 border-b border-white/20 font-semibold tracking-wide text-[12px] uppercase">Address 2</th>
                    <th className="text-left p-3 border-b border-white/20 font-semibold tracking-wide text-[12px] uppercase">City</th>
                    <th className="text-left p-3 border-b border-white/20 font-semibold tracking-wide text-[12px] uppercase">State</th>
                    <th className="text-left p-3 border-b border-white/20 font-semibold tracking-wide text-[12px] uppercase">Pincode</th>
                    <th className="text-left p-3 border-b border-white/20 w-32 font-semibold tracking-wide text-[12px] uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {masters.map((item, idx) => (
                    <tr key={item.id} className={`border-b border-indigo-50 hover:bg-gradient-to-r hover:from-indigo-50 hover:to-violet-50 transition-all duration-180 ${idx % 2 === 0 ? 'bg-white' : 'bg-gradient-to-r from-indigo-50/40 to-violet-50/30'}`}>
                      {editingId === item.id ? (
                        <>
                          <td className="p-2 text-gray-500">{idx + 1}</td>
                          <td className="p-2">
                            <input type="text" className="w-full p-1 border rounded text-sm" value={editData.name}
                              onChange={(e) => setEditData(prev => ({...prev, name: e.target.value}))} />
                          </td>
                          <td className="p-2">
                            <input type="tel" maxLength={10} className="w-full p-1 border rounded text-sm" value={editData.emp_mobile || ''}
                              onChange={(e) => setEditData(prev => ({...prev, emp_mobile: e.target.value.replace(/\D/g, '')}))} />
                          </td>
                          <td className="p-2">
                            <input type="email" className="w-full p-1 border rounded text-sm" value={editData.emp_email || ''}
                              onChange={(e) => setEditData(prev => ({...prev, emp_email: e.target.value}))} />
                          </td>
                          <td className="p-2">
                            <input type="text" className="w-full p-1 border rounded text-sm" value={editData.emp_door_no || ''}
                              onChange={(e) => setEditData(prev => ({...prev, emp_door_no: e.target.value}))} />
                          </td>
                          <td className="p-2">
                            <input type="text" className="w-full p-1 border rounded text-sm" value={editData.emp_address_1 || ''}
                              onChange={(e) => setEditData(prev => ({...prev, emp_address_1: e.target.value}))} />
                          </td>
                          <td className="p-2">
                            <input type="text" className="w-full p-1 border rounded text-sm" value={editData.emp_address_2 || ''}
                              onChange={(e) => setEditData(prev => ({...prev, emp_address_2: e.target.value}))} />
                          </td>
                          <td className="p-2">
                            <input type="text" className="w-full p-1 border rounded text-sm" value={editData.emp_city || ''}
                              onChange={(e) => setEditData(prev => ({...prev, emp_city: e.target.value}))} />
                          </td>
                          <td className="p-2">
                            <input type="text" className="w-full p-1 border rounded text-sm" value={editData.emp_state || ''}
                              onChange={(e) => setEditData(prev => ({...prev, emp_state: e.target.value}))} />
                          </td>
                          <td className="p-2">
                            <input type="text" maxLength={6} className="w-full p-1 border rounded text-sm" value={editData.emp_pincode || ''}
                              onChange={(e) => setEditData(prev => ({...prev, emp_pincode: e.target.value.replace(/\D/g, '')}))} />
                          </td>
                          <td className="p-2">
                            <div className="flex gap-2">
                              <button onClick={() => handleSaveEdit(item.id)} className="p-1 text-green-600 hover:text-green-800 hover:bg-green-50 rounded" title="Save">
                                <Check className="w-5 h-5" />
                              </button>
                              <button onClick={handleCancelEdit} className="p-1 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded" title="Cancel">
                                <X className="w-5 h-5" />
                              </button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="p-3 text-gray-500">{idx + 1}</td>
                          <td className="p-3 font-medium">{item.name}</td>
                          <td className="p-3">{item.emp_mobile || '-'}</td>
                          <td className="p-3 text-blue-600">{item.emp_email || '-'}</td>
                          <td className="p-3">{item.emp_door_no || '-'}</td>
                          <td className="p-3">{item.emp_address_1 || '-'}</td>
                          <td className="p-3">{item.emp_address_2 || '-'}</td>
                          <td className="p-3">{item.emp_city || '-'}</td>
                          <td className="p-3">{item.emp_state || '-'}</td>
                          <td className="p-3">{item.emp_pincode || '-'}</td>
                          <td className="p-3">
                            <div className="flex gap-2">
                              {canUpdate(RESOURCES.MASTERS) && (
                                <button onClick={() => handleEdit(item)} className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded" title="Edit">
                                  <Pencil className="w-4 h-4" />
                                </button>
                              )}
                              {canDelete(RESOURCES.MASTERS) && (
                                <button onClick={() => handleDelete(item.id, item.name)} className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded" title="Delete">
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-center text-gray-500 py-8">No service technicians added yet. Add your first technician to get started.</p>
          )
        )}
      </div>
    </SalesLayout>
  );
}

export default MastersManagement;
